import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Breadcrumbs, Button, Divider, showToast } from '@diamond/shared/ui';
import { AdminLayout, ExcelFileDropzone } from '@diamond/sol-admin/components';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { downloadBlob, getApiErrorMessage } from '@diamond/sol-admin/utils';
import { CloudDownloadOutlined, UploadFileOutlined } from '@mui/icons-material';
import { useCallback, useMemo, useState } from 'react';

import {
  exportProductLimitation,
  importProductLimitation,
  PRODUCT_LIMITATION_CONFIGURATION_META,
  ProductLimitationConfigurationType,
} from '../api';

const BREADCRUMBS = [
  {
    title: 'Beranda',
    link: '/',
  },
  {
    title: 'Product Limitation Order',
    link: '/product-limitation',
  },
];

const PRODUCT_LIMITATION_CONFIGURATION_OPTIONS: Array<{
  type: ProductLimitationConfigurationType;
  tabLabel: string;
  uploadTitle: string;
  description: string;
  helperMessage: string;
  isQuantityRelated: boolean;
}> = [
  {
    type: 'GLOBAL_DISTRIBUTION_CHANNEL',
    tabLabel: 'Global Config (Distribution Channel)',
    uploadTitle: 'Upload Global Configuration',
    description:
      'Upload file Excel untuk konfigurasi Product Limitation global pada level distribution channel.',
    helperMessage:
      'Mode ini digunakan untuk mengatur quantity limit produk secara global per distribution channel.',
    isQuantityRelated: true,
  },
  {
    type: 'EXCLUDE_SHIPTO',
    tabLabel: 'Exclude Ship-to Config',
    uploadTitle: 'Upload Exclude Ship-to Configuration',
    description:
      'Upload file Excel untuk konfigurasi ship-to yang di-exclude dari Product Limitation produk tertentu.',
    helperMessage:
      'Mode ini digunakan untuk menetapkan daftar ship-to yang di-exclude dari aturan Product Limitation.',
    isQuantityRelated: false,
  },
  {
    type: 'SHIPTO',
    tabLabel: 'Ship-to Config',
    uploadTitle: 'Upload Ship-to Configuration',
    description:
      'Upload file Excel untuk konfigurasi quantity limit produk tertentu per ship-to.',
    helperMessage:
      'Mode ini digunakan untuk mengatur quantity limit produk tertentu untuk ship-to tertentu.',
    isQuantityRelated: true,
  },
];

export function ProductLimitationPage() {
  const toast = useToast();
  const session = useAuthStore();

  const [file, setFile] = useState<File | null>(null);
  const [configurationType, setConfigurationType] =
    useState<ProductLimitationConfigurationType>('SHIPTO');
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const selectedConfiguration = useMemo(() => {
    const option = PRODUCT_LIMITATION_CONFIGURATION_OPTIONS.find(
      (item) => item.type === configurationType
    );

    if (option) {
      return option;
    }

    return PRODUCT_LIMITATION_CONFIGURATION_OPTIONS[2];
  }, [configurationType]);

  const activeTabIndex = useMemo(() => {
    return PRODUCT_LIMITATION_CONFIGURATION_OPTIONS.findIndex(
      (item) => item.type === configurationType
    );
  }, [configurationType]);

  const isBusy = isUploading || isDownloading;

  const handleDropzoneError = useCallback(
    (message: string) => {
      showToast(toast, 'error', message);
    },
    [toast]
  );

  const handleDownloadReference = useCallback(async () => {
    setIsDownloading(true);

    try {
      const blob = await exportProductLimitation(
        session.access_token,
        configurationType
      );
      downloadBlob(
        blob,
        PRODUCT_LIMITATION_CONFIGURATION_META[configurationType].exportFilename
      );
      showToast(toast, 'success', 'Template file berhasil diunduh');
    } catch (error) {
      showToast(
        toast,
        'error',
        getApiErrorMessage(error, 'Gagal mengunduh template file')
      );
    } finally {
      setIsDownloading(false);
    }
  }, [configurationType, session.access_token, toast]);

  const handleUpload = useCallback(async () => {
    if (!file) {
      showToast(toast, 'error', 'Silakan pilih file terlebih dahulu');
      return;
    }

    setIsUploading(true);

    try {
      const response = await importProductLimitation(
        session.access_token,
        file,
        configurationType
      );
      const importedCount = response.data?.imported_count ?? 0;
      const message =
        importedCount > 0
          ? `${response.message} (${importedCount} baris berhasil diimpor)`
          : response.message;

      showToast(toast, 'success', message || 'File berhasil di-upload');
      setFile(null);
    } catch (error) {
      showToast(
        toast,
        'error',
        getApiErrorMessage(error, 'Gagal meng-upload file Product Limitation')
      );
    } finally {
      setIsUploading(false);
    }
  }, [configurationType, file, session.access_token, toast]);

  const handleConfigurationTabChange = useCallback((tabIndex: number) => {
    const option = PRODUCT_LIMITATION_CONFIGURATION_OPTIONS[tabIndex];

    if (!option) {
      return;
    }

    setConfigurationType(option.type);
    setFile(null);
  }, []);

  return (
    <AdminLayout>
      <Box pb={4}>
        <Breadcrumbs links={BREADCRUMBS} useRouterDom />
      </Box>

      <Flex justify="space-between" align="center" gap={4} wrap="wrap">
        <Box>
          <Text fontSize="2xl" as="b" color="gray.800">
            Product Limitation Order
          </Text>
          <Text fontSize="sm" color="gray.600" mt={1}>
            Kelola upload Global Config, Exclude Ship-to, dan Ship-to Config
            dalam satu halaman.
          </Text>
        </Box>

        <HStack spacing={3}>
          <Button
            variant="outline"
            onClick={handleDownloadReference}
            isLoading={isDownloading}
            isDisabled={isUploading}
          >
            <HStack spacing={2}>
              <Icon as={CloudDownloadOutlined} boxSize={4} />
              <Text>Download Template</Text>
            </HStack>
          </Button>
          <Button
            variant="solid"
            onClick={handleUpload}
            isLoading={isUploading}
            isDisabled={!file || isDownloading}
          >
            <HStack spacing={2}>
              <Icon as={UploadFileOutlined} boxSize={4} />
              <Text>Upload File</Text>
            </HStack>
          </Button>
        </HStack>
      </Flex>

      <Stack pt={4} pb={8}>
        <Divider />
      </Stack>

      <Stack
        spacing={4}
        border="1px"
        borderRadius="md"
        borderColor="gray.200"
        bg="white"
        px={{ base: 3, md: 5 }}
        py={{ base: 4, md: 5 }}
        boxShadow="sm"
      >
        <Tabs
          index={activeTabIndex}
          onChange={handleConfigurationTabChange}
          isLazy
        >
          <TabList
            overflowX="auto"
            gap={2}
            pb={2}
            borderBottom="1px"
            borderColor="gray.200"
          >
            {PRODUCT_LIMITATION_CONFIGURATION_OPTIONS.map((option) => (
              <Tab
                key={option.type}
                fontSize="sm"
                fontWeight="semibold"
                border="1px"
                borderColor="gray.200"
                bg="white"
                color="gray.700"
                borderRadius="md"
                whiteSpace="nowrap"
                px={4}
                py={2}
                isDisabled={isBusy}
                _selected={{
                  bg: '#2563EB',
                  color: '#FFFFFF',
                  borderColor: '#2563EB',
                  _hover: {
                    bg: '#1D4ED8',
                    color: '#FFFFFF',
                  },
                }}
                _hover={{
                  bg: '#F3F4F6',
                  color: '#111827',
                }}
              >
                {option.tabLabel}
              </Tab>
            ))}
          </TabList>
        </Tabs>

        <Box
          border="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          bg="gray.50"
        >
          <Stack spacing={2}>
            <Text fontSize="lg" as="b" color="gray.800">
              {selectedConfiguration.uploadTitle}
            </Text>
            <Text fontSize="sm" color="gray.700">
              {selectedConfiguration.description}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {selectedConfiguration.helperMessage}
            </Text>
            <HStack spacing={2} flexWrap="wrap" pt={1}>
              <Badge colorScheme="purple" px={2} py={1} borderRadius="md">
                Format xlsx
              </Badge>
              <Badge colorScheme="orange" px={2} py={1} borderRadius="md">
                Max 2MB
              </Badge>
              <Badge colorScheme="teal" px={2} py={1} borderRadius="md">
                1 file per upload
              </Badge>
            </HStack>
          </Stack>
        </Box>

        {selectedConfiguration.isQuantityRelated && (
          <Alert
            status="warning"
            borderRadius="md"
            alignItems="flex-start"
            bg="#FFF7ED"
            border="1px solid"
            borderColor="#FED7AA"
            px={4}
            py={3}
          >
            <AlertIcon mt={0.5} color="#C2410C" />
            <Text
              fontSize="sm"
              color="#9A3412"
              lineHeight="1.5"
              fontWeight="medium"
            >
              Perhatian: Nilai stock pada file menggunakan magnify x1000.
              Pastikan quantity yang diinput mengikuti satuan tersebut.
            </Text>
          </Alert>
        )}

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            File Product Limitation
          </FormLabel>
          <ExcelFileDropzone
            file={file}
            onFileChange={setFile}
            onFileRejected={handleDropzoneError}
            isDisabled={isDownloading}
            isUploading={isUploading}
          />
        </FormControl>
      </Stack>
    </AdminLayout>
  );
}
